import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ToasterType, ToasterService } from '@tt-chvac/core';
import { UserInfoService } from '@tt-chvac/core-sales-web';
import { AppConstants } from '../../../shared/constants/constants';
import { KeyTeamMembersService } from '../../services/key-team-members.service';
import { ICreditProjectPreference, IUserPeferences } from '../../model/key-team-members-model';
import { ActiveDirectoryComponent } from '@tt-chvac/core-sales-web';

@Component({
    selector: 'app-key-team-members',
    templateUrl: './key-team-members.component.html',
    styleUrls: ['./key-team-members.component.scss'],
    standalone: false
})
export class KeyTeamMembersComponent implements OnInit, AfterViewInit {
  projectMgrName = 'projectMgrName';
  salesMgrName = 'salesMgrName';
  formMode = 'create';
  isFormEditable = true;
  isMandatory = false;
  showLoader = false;
  doesUserPreferenceExists = false;
  public projectManagerUserID: string;
  public salesManagerName: string;
  public projectManagerName: string;
  public keyTeamMemberForm: UntypedFormGroup;
  @ViewChild('salesMgrName') salesMgrNameActiveDirectory: ActiveDirectoryComponent;
  @ViewChild('projectMgrName') projectMgrNameActiveDirectory: ActiveDirectoryComponent;

  constructor(
    public fb: UntypedFormBuilder,
    public userInfoService: UserInfoService,
    public keyTeamMembersService: KeyTeamMembersService,
    public toasterService: ToasterService,
    public appConstants: AppConstants) { }

  ngOnInit(): void {
    this.createOfficePeopleForm();
    this.getUserPeferences();
  }

  ngAfterViewInit(): void {
    this.clearActiveDirectoryValidators();
  }

  // Creates the office and people form
  createOfficePeopleForm(): void {
    this.keyTeamMemberForm = this.fb.group({
      projectMgrName: ['', Validators.required],
      salesMgrName: ['', Validators.required]
    });
  }

  // Get user peferences
  getUserPeferences(): void {
    const userId = this.userInfoService.getUserId();
    this.showLoader = true;
    this.keyTeamMembersService.fetchUserPeferences(userId).subscribe((userpredata: IUserPeferences) => {
      this.showLoader = false;
      if (userpredata !== null) {
        this.doesUserPreferenceExists = true;
        this.salesManagerName = userpredata.salesManager;
        this.projectManagerName = userpredata.projectManager;
        this.keyTeamMemberForm.controls[this.salesMgrName].setValue(this.salesManagerName);
        this.keyTeamMemberForm.controls[this.projectMgrName].setValue(this.projectManagerName);
        this.syncActiveDirectoryDefaults();
      } else {
        this.doesUserPreferenceExists = false;
        this.salesManagerName = null;
        this.projectManagerName = null;
      }
      this.clearActiveDirectoryValidators();
    }, (err) => {
      this.showLoader = false;
      this.salesManagerName = null;
      this.projectManagerName = null;
    });
  }

  /**
   * Set project manager name from active directory form.
   * @param formGroupValue contains form child section.
   */
  setProjectManagerName(formGroupValue: UntypedFormGroup, selectedDetails): void {
    if (selectedDetails) {
      this.projectManagerUserID = selectedDetails.onPremisesSamAccountName;
    }
    const activeDirectoryValue = formGroupValue?.getRawValue()?.activeDirectory || '';
    this.keyTeamMemberForm.controls[this.projectMgrName].setValue(activeDirectoryValue);
    this.keyTeamMemberForm.controls[this.projectMgrName].markAsTouched();
  }

  /**
   * Set project manager name from active directory form.
   * @param formGroupValue contains form child section.
   */
  setSalesManagerName(formGroupValue: UntypedFormGroup, selectedDetails): void {
    const activeDirectoryValue = formGroupValue?.getRawValue()?.activeDirectory || '';
    this.keyTeamMemberForm.controls[this.salesMgrName].setValue(activeDirectoryValue);
    this.keyTeamMemberForm.controls[this.salesMgrName].markAsTouched();
  }

  /**
   * Clears the validators for the project manager and sales manager fields.
   */
  clearActiveDirectoryValidators(): void {
    const projectManagerControl = this.projectMgrNameActiveDirectory?.activeDirectoryFormControls?.['activeDirectory'];
    const salesManagerControl = this.salesMgrNameActiveDirectory?.activeDirectoryFormControls?.['activeDirectory'];
    if (projectManagerControl) {
      projectManagerControl.clearValidators();
      projectManagerControl.updateValueAndValidity();
    }
    if (salesManagerControl) {
      salesManagerControl.clearValidators();
      salesManagerControl.updateValueAndValidity();
    }
  }

  /**
   * Saves the case summary detail.
   */
  saveCreditProjectPreference(): void {
    const projectManagerName = this.keyTeamMemberForm.controls[this.projectMgrName].value;
    const salesManagerName = this.keyTeamMemberForm.controls[this.salesMgrName].value;
    if (this.doesUserPreferenceExists ||
       (!this.doesUserPreferenceExists && projectManagerName && projectManagerName.length > 0) ||
       (!this.doesUserPreferenceExists && salesManagerName && salesManagerName.length > 0)) {
      const creditProjectPreferencePayload: ICreditProjectPreference = {
        id: null,
        userId: this.userInfoService.getUserId(),
        projectManager: this.keyTeamMemberForm.controls[this.projectMgrName].value,
        salesManager: this.keyTeamMemberForm.controls[this.salesMgrName].value,
        projectManagerUserID: this.projectManagerUserID
      };
      this.clearActiveDirectoryValidators();
      this.showLoader = true;
      this.keyTeamMembersService.saveCreditProjectPreference(creditProjectPreferencePayload).subscribe((response: string) => {
        this.showLoader = false;
        this.toasterService.setToaster(ToasterType.SUCCESS, this.appConstants.KEY_TEAM_MEMBERS_SAVE_SUCCESS_MESSAGE);
      }, (error: HttpErrorResponse) => {
        this.showLoader = false;
        this.toasterService.setToaster(ToasterType.ERROR, error.error);
      });
    } else {
      const projectManagerControl = this.projectMgrNameActiveDirectory?.activeDirectoryFormControls?.['activeDirectory'];
      const salesManagerControl = this.salesMgrNameActiveDirectory?.activeDirectoryFormControls?.['activeDirectory'];
      projectManagerControl?.setValidators(Validators.required);
      salesManagerControl?.setValidators(Validators.required);
      projectManagerControl?.updateValueAndValidity();
      salesManagerControl?.updateValueAndValidity();
    }
  }

  /**
   * Synchronizes default manager names into child active-directory components when available.
   */
  private syncActiveDirectoryDefaults(): void {
    if (!this.salesMgrNameActiveDirectory || !this.projectMgrNameActiveDirectory) {
      return;
    }

    this.salesMgrNameActiveDirectory.defaultManagerName = this.salesManagerName;
    this.salesMgrNameActiveDirectory.setActiveDirectoryControlValue();
    this.projectMgrNameActiveDirectory.defaultManagerName = this.projectManagerName;
    this.projectMgrNameActiveDirectory.setActiveDirectoryControlValue();
  }
}
